import type { Channel, ResolvedVendor } from './vendor-channel-resolver.service';

/**
 * A vendor's declared channels as the registry holds them: `undefined` = no vendordb
 * record, `null` or `[]` = a record that is not yet classified. Non-throwing by
 * contract, so callers pass a plain `registry.get(vendor)?.channels` rather than
 * VendorChannelResolver.channelsOf(), which throws on a missing entry.
 */
export type ChannelDeclarationLookup = (vendor: string) => ReadonlyArray<string> | null | undefined;

/** An observation the vendor's declared capabilities do not account for (ADR-0006). */
export type CapabilityDefect = {
  readonly kind:
    | 'undeclared-channel'
    | 'unregistered-vendor'
    | 'undeclared-price-convention'
    | 'price-convention-contradicted';
  readonly vendor: string;
  readonly channel: Channel;
};

const PAIR_SEPARATOR = '\u0000';

/**
 * Report each distinct (vendor, channel) pair a decoded batch observed that the vendor's
 * vendordb record does not declare, at most once per pair. Never throws and never
 * filters -- ADR-0006 owns the rationale for describing rather than acting.
 */
export function findCapabilityDefects(
  observed: Iterable<ResolvedVendor>,
  declaredChannelsOf: ChannelDeclarationLookup
): readonly CapabilityDefect[] {
  const defects: CapabilityDefect[] = [];
  const seen = new Set<string>();

  for (const { vendor, channel } of observed) {
    if (channel === 'aggregate') continue;

    const pair = `${vendor}${PAIR_SEPARATOR}${channel}`;
    if (seen.has(pair)) continue;
    seen.add(pair);

    const declared = declaredChannelsOf(vendor);
    if (declared === undefined) {
      defects.push({ kind: 'unregistered-vendor', vendor, channel });
      continue;
    }
    if (declared === null || declared.length === 0) continue;
    if (!declared.includes(channel)) {
      defects.push({ kind: 'undeclared-channel', vendor, channel });
    }
  }

  return defects;
}
